/**
 * Conversations page — /brand/conversations
 *
 * The Marque view, split by a centred toggle into « Tableau de bord » (all the metrics)
 * and « Configurer » (the « Personas » list). A row opens /brand/conversations/:personaId.
 *
 * §4.5.0 — gated behind `conversationsEnabled` flag AND `conversation_personas > 0`.
 * §4.5.1 — shared chrome: period selector, create button.
 * §4.5.2 — Marque view: dashboard = charts + top 10; setup = couverture, pondération,
 *          priorités, personas list.
 *
 * Lots 4+5: analysis status shown in matrix; brand score computed client-side.
 */

import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,

  Select,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../services/api';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { resolveLimits } from '@traaker/shared';
import {
  CONVERSATION_MAX_PERSONAS_PER_BRAND,
  CONVERSATION_INTENTS,
  type ConversationPersona,
  type ConversationIntentId,
  type ConversationBrandRollup,
  type ConversationPersonaSummary,
  type ConversationWindow,
} from '@traaker/shared';
import { PersonaEditorModal } from '../components/conversations/PersonaEditorModal';
import ConversationsDashboard from '../components/conversations/ConversationsDashboard';
import { hasLaunchToday } from '../components/conversations/supersede';
import { EngineLogo } from '../components/brand/EngineLogo';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MixWeights { learn: number; discover: number; compare: number }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const INTENT_COLORS: Record<ConversationIntentId, string> = {
  learn: '#0f7c8c',
  discover: '#c47a12',
  compare: '#b83280',
};

function intentLabel(id: ConversationIntentId, t: (key: string) => string) {
  const map: Record<ConversationIntentId, string> = {
    learn: t('conversations.intentLearn'),
    discover: t('conversations.intentDiscover'),
    compare: t('conversations.intentCompare'),
  };
  return map[id] ?? id;
}

function statusLabel(status: string, t: (key: string) => string) {
  if (status === 'active') return t('conversations.cardStatusActive');
  if (status === 'paused') return t('conversations.cardStatusPaused');
  if (status === 'archived') return t('conversations.cardStatusArchived');
  return status;
}

function statusColor(status: string): 'success' | 'warning' | 'default' {
  if (status === 'active') return 'success';
  if (status === 'paused') return 'warning';
  return 'default';
}

// ---------------------------------------------------------------------------
// Persona row — the ⑩ « Personas » panel of the Marque view (§4.5.2)
// ---------------------------------------------------------------------------

/** GET .../launches/:launchId/progress (turn-level) */
interface LaunchProgress {
  launchId: string;
  /** Every cell terminal AND finalized — results readable, launch over. */
  done: boolean;
  /** Turns over, summaries still being written. */
  finalizing: boolean;
  totalTurns: number;
  doneTurns: number;
}

/**
 * Ceiling on `/progress` calls per poll tick. Progress is per launch, so it costs
 * one request per running persona — fine at 3, not at 50 (a brand may hold up to
 * CONVERSATION_MAX_PERSONAS_PER_BRAND, and a cadence tick can make many due at
 * once). Statuses come from a single list read instead, so the cards past this cap
 * still leave `running`; they just show no live turn counter while they work.
 */
const MAX_PROGRESS_POLLS = 6;

/** P · C · G · GA · M — Perplexity, ChatGPT, Gemini, Google AI, Mistral. */
const ENGINE_COLUMNS: { key: string; label: string }[] = [
  { key: 'perplexity', label: 'Perplexity' },
  { key: 'chatgpt', label: 'ChatGPT' },
  { key: 'gemini', label: 'Gemini' },
  { key: 'google', label: 'Google AI' },
  { key: 'mistral', label: 'Mistral' },
];

type PersonaSlice = ConversationPersonaSummary['windows'][ConversationWindow];

/**
 * What the row may honestly claim about this persona.
 *
 * The list is driven by the persona CONFIG rows (which always exist); the `PSUM#`
 * measurement row is an enrichment that only lands once a launch has completed and
 * the rollup has run. A persona without one is never omitted and never shown as 0 %.
 */
type MeasureState = 'measured' | 'noRunsInWindow' | 'measuring' | 'notMeasuredYet';

function measureState(p: ConversationPersona, slice: PersonaSlice | undefined): MeasureState {
  if (slice && slice.n > 0) return 'measured';
  if (slice) return 'noRunsInWindow';
  // nextRunAt alone (scheduled but never launched) must NOT read as "measuring":
  // that misleads a newly created persona into showing "en cours de mesure" with 0 runs.
  if (p.lastLaunchStatus === 'running' || p.lastLaunchAt) return 'measuring';
  return 'notMeasuredYet';
}

interface PersonaRowProps {
  persona: ConversationPersona;
  /** The `PSUM#` row, when the rollup has written one. */
  summary?: ConversationPersonaSummary;
  window: ConversationWindow;
  /** An active intent tab already names the intent: the pill would be redundant. */
  showIntentPill: boolean;
  canRunNow: boolean;
  /** Present while the persona's last launch is in flight. */
  progress?: LaunchProgress | null;
  onEdit: (p: ConversationPersona) => void;
  onDuplicate: (p: ConversationPersona) => void;
  onDelete: (p: ConversationPersona) => void;
  onRunNow: (p: ConversationPersona) => void;
}

// memo: a progress tick must repaint only the row whose props changed.
const PersonaRow = memo(function PersonaRow({
  persona, summary, window: w, showIntentPill, canRunNow, progress,
  onEdit, onDuplicate, onDelete, onRunNow,
}: PersonaRowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const slice = summary?.windows?.[w];
  const state = measureState(persona, slice);
  const isRunning = persona.lastLaunchStatus === 'running';
  const pctDone = progress && progress.totalTurns > 0 ? Math.round((progress.doneTurns / progress.totalTurns) * 100) : 0;
  const openDetail = () => navigate(`/brand/conversations/${persona.personaId}`);

  const presence = slice?.presence ?? null;
  const prevPresence = slice?.prev?.presence ?? null;
  const delta = presence !== null && prevPresence !== null ? Math.round((presence - prevPresence) * 100) : null;
  const outcome = state === 'measured' && slice ? slice.outcome : null;
  const outcomeTotal = outcome ? outcome.win + outcome.part + outcome.lose : 0;
  /**
   * Days that actually held a launch, newest last — the columns of the Résultat chart.
   * A day with `total === 0` is dropped rather than drawn as an empty slot: an absent
   * day is "we did not measure", which a grey column would misread as "we lost".
   */
  const outcomeDays = (summary?.outcomeSpark ?? [])
    .map((o, i) => ({ i, o, total: o ? o.win + o.part + o.lose : 0 }))
    .filter((d): d is { i: number; o: { win: number; part: number; lose: number }; total: number } =>
      d.o !== null && d.total > 0);
  const zones = [...new Set(persona.cells.map((c) => c.zoneMeta?.label ?? c.zone))];
  const intentColor = INTENT_COLORS[persona.intentId];

  /** « en cours de mesure » etc. — an empty state that says what is empty. */
  const notMeasuredLabel =
    state === 'noRunsInWindow' ? t('conversations.personasTableNoRunsInWindow')
    : state === 'measuring' ? t('conversations.personasTableMeasuring')
    : t('conversations.personasTableNotMeasuredYet');

  return (
    <TableRow hover onClick={openDetail} sx={{ cursor: 'pointer' }}>
      {/* Persona — name + psub chips */}
      <TableCell sx={{ maxWidth: 280 }}>
        <Button
          variant="text"
          onClick={(e) => { e.stopPropagation(); openDetail(); }}
          sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600, textAlign: 'left', lineHeight: 1.3 }}
        >
          {persona.name}
        </Button>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 0.25 }}>
          {showIntentPill && (
            <Chip
              label={intentLabel(persona.intentId, t)}
              size="small"
              sx={{
                fontSize: '0.625rem', height: 18, fontWeight: 600,
                bgcolor: `color-mix(in srgb, ${intentColor} 16%, white)`,
                color: intentColor,
                border: '1px solid ' + intentColor + '40',
              }}
            />
          )}
          {persona.status !== 'active' && (
            <Chip
              label={statusLabel(persona.status, t)}
              size="small"
              color={statusColor(persona.status)}
              variant="outlined"
              sx={{ fontSize: '0.625rem', height: 18 }}
            />
          )}
          {/*
            Zone COUNT, not the zone names (2026-10-03). Three city labels plus a « +N »
            took the width of the whole cell to say something the user already configured
            and cannot act on from here; the names are one click away on the persona page,
            and the per-zone numbers live in « Détail du score ». The names stay in the
            tooltip so nothing is actually lost.
          */}
          {zones.length > 0 && (
            <Tooltip title={zones.join(' · ')}>
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'default' }}>
                {t('conversations.personasTableZoneCount', { count: zones.length })}
              </Typography>
            </Tooltip>
          )}
          {/* Cadence, when there is one — absent rather than a dash when there is not. */}
          {persona.cadence && persona.cadence !== 'off' && (
            <Typography variant="caption" color="text.secondary">
              {t(`conversations.cadence${
                (persona.cadence as { unit: string }).unit === 'days' ? 'Daily'
                  : (persona.cadence as { unit: string }).unit === 'weeks' ? 'Weekly' : 'Monthly'
              }`)}
            </Typography>
          )}
          {state !== 'measured' && persona.nextRunAt && (
            <Chip
              label={t('conversations.personaPendingZone', {
                date: new Date(persona.nextRunAt).toLocaleDateString('fr-FR'),
              })}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.625rem', height: 18 }}
            />
          )}
        </Box>
        {/* Last run date — only when not currently running */}
        {!isRunning && persona.lastLaunchAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {t('conversations.personaLastRun', {
              date: new Date(persona.lastLaunchAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }),
              time: new Date(persona.lastLaunchAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
            })}
          </Typography>
        )}
        {/* Live launch progress (turns) */}
        {isRunning && (
          <Box sx={{ mt: 0.75, maxWidth: 240 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {progress?.finalizing
                ? t('conversations.progressFinalizing')
                : progress && progress.doneTurns > 0
                  ? `${t('conversations.progressTurns', { done: progress.doneTurns, total: progress.totalTurns })} · ${pctDone} %`
                  : t('conversations.progressWaiting')}
            </Typography>
            <LinearProgress
              variant={progress && progress.doneTurns > 0 ? 'determinate' : 'indeterminate'}
              value={pctDone}
              sx={{ height: 4, borderRadius: 2, mt: 0.25 }}
            />
          </Box>
        )}
      </TableCell>

      {/* Présence */}
      <TableCell sx={{ minWidth: 120 }}>
        {state === 'measured' && presence !== null ? (
          <>
            <Typography variant="body2" fontWeight={700} component="span">
              {Math.round(presence * 100)} %
            </Typography>
            {delta !== null && (
              <Typography
                variant="caption"
                sx={{ ml: 0.75, color: delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary' }}
              >
                {delta > 0 ? '+' : ''}{delta} pts
              </Typography>
            )}
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.round(presence * 100))}
              sx={{ height: 5, borderRadius: 3, mt: 0.5, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { bgcolor: intentColor } }}
            />
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">{notMeasuredLabel}</Typography>
        )}
      </TableCell>

      {/*
        14 jours — a polyline over the daily presences, with gaps for unmeasured days.

        An ISOLATED day gets a dot (2026-10-03). The chart only ever drew segments
        between two consecutive non-null days, so a persona with a single launch —
        i.e. every persona right after its first run — rendered an empty 92×28 box
        that read as "we measured nothing". The dot is a `rect`, not a `circle`:
        `preserveAspectRatio="none"` scales x by ~7 and y by 1, which would squash a
        circle into a sliver.
      */}
      <TableCell sx={{ minWidth: 100 }}>
        {(() => {
          const spark = summary?.spark;
          if (!spark) return null;
          const pts: { i: number; v: number }[] = [];
          spark.forEach((v, i) => { if (v !== null) pts.push({ i, v }); });
          if (pts.length === 0) return null;
          const W = Math.max(1, spark.length - 1);
          const toY = (v: number) => Math.max(1, Math.min(27, 28 - v * 28));
          if (pts.length === 1) {
            const cx = W <= 1 ? 0.5 : pts[0].i;
            return (
              <svg width="92" height="28" viewBox={`0 0 ${W} 28`} preserveAspectRatio="none">
                <circle cx={cx} cy={toY(pts[0].v)} r={Math.max(0.5, W * 2.5 / 92)} fill={intentColor} />
              </svg>
            );
          }
          let d = '';
          for (const { i, v } of pts) {
            d += d === '' ? `M${i},${toY(v).toFixed(1)}` : `L${i},${toY(v).toFixed(1)}`;
          }
          return (
            <svg width="92" height="28" viewBox={`0 0 ${W} 28`} preserveAspectRatio="none">
              <path
                d={d}
                fill="none"
                stroke={intentColor}
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
          );
        })()}
      </TableCell>

      {/*
        Moteurs — one slot per engine, logo + its presence. `null` is absent, never 0.

        This was five abstract heat-squares (opacity = presence) under the header
        « Par moteur (P · C · G · GA · M) ». On a persona that tracks a subset of
        engines, every slot rendered as the same hatched square, so the column read as
        decorative noise: nothing on screen said WHICH engine a square was, and the
        legend was five initials in a header the eye never returns to. The logo is the
        identifier users already know from Brand Perception, so it carries the column
        without a legend — and the number is printed rather than encoded as opacity,
        because an opacity ramp is not readable to within 20 points anyway.

        The five slots are still FIXED and in the same order on every row: that is what
        makes the column scannable down a list, and it is also how an untracked engine
        stays visible as a gap instead of silently shifting its neighbours.
      */}
      <TableCell>
        {state === 'measured' && (
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {ENGINE_COLUMNS.map(({ key, label }) => {
              // `byEngine[key]` is a `{ n, presence }` pair, not a bare number — an
              // absent key means the engine is not tracked, `presence: null` means
              // tracked with nothing measured. Both read as "untracked" here.
              const ep = slice?.byEngine?.[key]?.presence;
              const measured = ep !== null && ep !== undefined;
              return (
                <Tooltip
                  key={key}
                  title={measured ? `${label} : ${Math.round(ep * 100)} %` : `${label} : ${t('conversations.detailNonSuivi')}`}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.125, cursor: 'default', minWidth: 26 }}>
                    {/* Untracked: desaturated and faded rather than removed — the slot
                        has to stay, and grayscale + opacity reads as "off" without
                        inventing a second icon. */}
                    <Box sx={{ display: 'flex', filter: measured ? 'none' : 'grayscale(1)', opacity: measured ? 1 : 0.3 }}>
                      <EngineLogo engine={key} size={18} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.625rem', lineHeight: 1, fontWeight: 600, color: measured ? 'text.primary' : 'text.disabled' }}
                    >
                      {measured ? `${Math.round(ep * 100)}%` : '—'}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </TableCell>

      {/* Part de voix */}
      <TableCell>
        {state === 'measured' && slice?.shareOfVoice !== null && slice?.shareOfVoice !== undefined && (
          <Typography variant="body2">{Math.round(slice.shareOfVoice * 100)} %</Typography>
        )}
      </TableCell>

      {/* Agents — total configured runs per launch */}
      <TableCell>
        {(() => {
          const totalRuns = persona.cells.reduce((s, c) => s + (c.runs ?? 0), 0);
          return totalRuns > 0 ? (
            <Typography variant="body2">{totalRuns}</Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          );
        })()}
      </TableCell>

      {/*
        Résultat — gagné / partiel / perdu, the verb varies by intent.

        The single stacked bar only ever said "where we are"; a persona that went from
        all-lost to half-won looked identical to one that never moved. So the bar became
        a stacked mini column per DAY (not per launch — a day holding two launches is one
        column, which is what keeps it index-aligned with the « 14 jours » presence
        sparkline next to it). The percentage caption stays: the chart shows the shape,
        the number is still the thing a user quotes.

        `outcomeSpark` is optional — PSUM# rows written before 2026-10-03 carry none,
        and a one-run persona has exactly one column, which is a column chart with
        nothing to compare. Both fall back to the original single bar rather than
        rendering a chart that implies a trend it cannot show.
      */}
      <TableCell sx={{ minWidth: 150 }}>
        {outcome && outcomeTotal > 0 && (
          <>
            {outcomeDays.length >= 2 ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 26 }}>
                {outcomeDays.map(({ i, o, total }) => (
                  <Tooltip
                    key={i}
                    title={t('conversations.personasTableOutcomeDay', {
                      won: Math.round((o.win / total) * 100),
                      part: Math.round((o.part / total) * 100),
                      lose: Math.round((o.lose / total) * 100),
                    })}
                  >
                    {/* Every column is full height: this is a composition chart, not a
                        volume chart. Scaling height by run count would make a quiet day
                        read as a bad day. */}
                    <Box sx={{ width: 7, height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 0.5, overflow: 'hidden', cursor: 'default' }}>
                      {o.win > 0 && <Box sx={{ flex: o.win, bgcolor: 'success.main' }} />}
                      {o.part > 0 && <Box sx={{ flex: o.part, bgcolor: 'warning.main' }} />}
                      {o.lose > 0 && <Box sx={{ flex: o.lose, bgcolor: 'grey.300' }} />}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden' }}>
                {outcome.win > 0 && <Box sx={{ flex: outcome.win, bgcolor: 'success.main' }} />}
                {outcome.part > 0 && <Box sx={{ flex: outcome.part, bgcolor: 'warning.main' }} />}
                {outcome.lose > 0 && <Box sx={{ flex: outcome.lose, bgcolor: 'grey.300' }} />}
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              {t('conversations.personasTableOutcome', {
                verb: CONVERSATION_INTENTS[persona.intentId]?.outcomes.verb ?? '',
                won: Math.round((outcome.win / outcomeTotal) * 100),
                part: Math.round((outcome.part / outcomeTotal) * 100),
              })}
            </Typography>
          </>
        )}
      </TableCell>

      {/* Actions — clicks here must not open the detail page */}
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title={isRunning ? t('conversations.progressRunNowBusy') : !canRunNow ? t('conversations.cardRunNowTooltipTier') : ''}>
            <span>
              <Button
                variant="contained"
                size="medium"
                disabled={!canRunNow || isRunning}
                onClick={() => onRunNow(persona)}
                startIcon={<PlayCircleOutlineIcon />}
              >
                {t('conversations.cardRunNow')}
              </Button>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label={t('conversations.cardEdit')}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => { setMenuAnchor(null); openDetail(); }}>
            {t('conversations.cardViewDetail')}
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onEdit(persona); }}>
            {t('conversations.cardEdit')}
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onDuplicate(persona); }}>
            {t('conversations.cardDuplicate')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setMenuAnchor(null); onDelete(persona); }} sx={{ color: 'error.main' }}>
            {t('conversations.cardDelete')}
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
});

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        px: 3,
        textAlign: 'center',
        gap: 2,
      }}
    >
      <ForumOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
      <Typography fontWeight={600} fontSize="1.125rem">
        {t('conversations.noPersonasTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
        {t('conversations.noPersonasBody')}
      </Typography>
      <Button variant="contained" onClick={onCreateClick}>
        {t('conversations.noPersonasCta')}
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Marque view — §4.5.2
// ---------------------------------------------------------------------------

const INTENT_IDS: ConversationIntentId[] = ['learn', 'discover', 'compare'];

/** Compute the brand score for the given mix weights and brand rollup rows.
 *  D39: uniform mean per intent; D37: weighted mix. No minimum persona count:
 *  every intent with a measured presence contributes.
 *  The score is NEVER in any API response — browser only. */
function computeBrandScore(
  rows: ConversationBrandRollup[],
  w: ConversationWindow,
  mix: MixWeights,
): { score: number | null; state: 'global' | 'none' } {
  const rowMap = Object.fromEntries(rows.map(r => [r.intentId, r]));
  const contributions: { presence: number; weight: number }[] = [];

  for (const intentId of INTENT_IDS) {
    const p = rowMap[intentId]?.windows?.[w]?.presence;
    if (p === null || p === undefined) continue;
    contributions.push({ presence: p, weight: mix[intentId] });
  }

  const tw = contributions.reduce((s, c) => s + c.weight, 0);
  if (tw === 0) return { score: null, state: 'none' };
  const score = contributions.reduce((s, c) => s + c.presence * c.weight, 0) / tw;
  return { score, state: 'global' };
}

interface MarqueViewProps {
  brandId: string;
  brandName: string;
  /** The persona CONFIG list — the panel ⑩ list is driven by this, not by `PSUM#`. */
  personas: ConversationPersona[];
  progressById: Record<string, LaunchProgress>;
  canRunNow: boolean;
  onEdit: (p: ConversationPersona) => void;
  onDuplicate: (p: ConversationPersona) => void;
  onDelete: (p: ConversationPersona) => void;
  onRunNow: (p: ConversationPersona) => void;
}

function MarqueView({
  brandId, brandName, personas, progressById, canRunNow,
  onEdit, onDuplicate, onDelete, onRunNow,
}: MarqueViewProps) {
  const { t } = useTranslation();


  // Period selector state (client-side toggle, no request)
  const [window, setWindow] = useState<ConversationWindow>('7d');

  const mix: MixWeights = { learn: 60, discover: 30, compare: 10 };
  const [top10Order, setTop10Order] = useState<'most' | 'least' | 'up' | 'down'>('most');
  const [personasTabIntent, setPersonasTabIntent] = useState<ConversationIntentId | 'all'>('all');
  const [personasSort, setPersonasSort] = useState<'presence' | 'won' | 'delta'>('presence');
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  const [tagSearch, setTagSearch] = useState('');

  // Data
  const [brandRows, setBrandRows] = useState<ConversationBrandRollup[]>([]);
  const [personasSummary, setPersonasSummary] = useState<ConversationPersonaSummary[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!brandId) return;
    setLoading(true);
    Promise.all([
      api.get<any[]>(`/brands/${brandId}/conversations/rollup`),
      api.get<{ items: any[]; nextCursor: string | null }>(`/brands/${brandId}/conversations/personas`),
    ]).then(([rollupItems, personasResp]) => {
      setBrandRows((rollupItems ?? []) as ConversationBrandRollup[]);
      setPersonasSummary((personasResp?.items ?? []) as ConversationPersonaSummary[]);
    }).catch(() => {
      // Show empty states
    }).finally(() => setLoading(false));
  }, [brandId]);

  /** Persona CONFIGS by id — the ⑧ top10 payload carries no `description`, so the
      tooltip reads it off the config list. A top10 entry can name an archived or
      deleted persona, which is simply absent here. */
  const personaById = useMemo(
    () => Object.fromEntries(personas.map(p => [p.personaId, p])),
    [personas],
  );

  /** `PSUM#` rows, by personaId — an enrichment of the config list, never its source. */
  const summaryById = useMemo(
    () => Object.fromEntries(personasSummary.map(s => [s.personaId, s])),
    [personasSummary],
  );

  // Panel ⑩ list: the configs, filtered by the intent tab and sorted by a measured
  // value when there is one. A persona with no measurement sorts last — never dropped.
  const personaRows = useMemo(() => {
    const value = (p: ConversationPersona): number | null => {
      const slice = summaryById[p.personaId]?.windows?.[window];
      if (!slice || slice.n === 0) return null;
      if (personasSort === 'presence') return slice.presence;
      if (personasSort === 'won') return slice.outcome.win / slice.n;
      const prev = slice.prev?.presence;
      return slice.presence !== null && prev !== null && prev !== undefined ? slice.presence - prev : null;
    };
    return personas
      .filter(p => personasTabIntent === 'all' || p.intentId === personasTabIntent)
      .filter(p => tagFilter.size === 0 || (p.tags ?? []).some(tag => tagFilter.has(tag)))
      .map(p => ({ persona: p, v: value(p) }))
      .sort((a, b) => (b.v ?? -Infinity) - (a.v ?? -Infinity))
      .map(x => x.persona);
  }, [personas, summaryById, window, personasSort, personasTabIntent, tagFilter]);

  const allTags = useMemo(() => {
    const s = new Set<string>();
    for (const p of personas) { for (const tag of (p.tags ?? [])) s.add(tag); }
    return [...s].sort();
  }, [personas]);

  // Legacy links still point at `/brand/conversations?tab=personas` (the two tabs are
  // gone). Land them on the persona list instead of breaking, then drop the param.
  const personasPanelRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Which of the two views is showing. Held in the URL, not in `useState`, so a
   * refresh keeps the tab the user was on (2026-10-03).
   *
   * The default is « Configurer » and the param is DELETED rather than set to
   * `setup`, which is what makes the sidebar entry behave: it links to a bare
   * `/brand/conversations`, so arriving from the nav always lands on Configurer
   * whatever the user was looking at before. It used to be
   * `viewChoice ?? (brandRows.length === 0 ? 'setup' : 'dashboard')`, which flipped
   * the landing tab the moment a brand rollup existed.
   *
   * `ScrollToTop` keys on `pathname` only, so toggling the tab does not scroll.
   */
  const storedView = localStorage.getItem('traaker.conversations.view');
  const view: 'dashboard' | 'setup' = searchParams.get('view') === 'dashboard' ? 'dashboard' : storedView === 'dashboard' ? 'dashboard' : 'setup';
  const setView = useCallback((v: 'dashboard' | 'setup') => {
    localStorage.setItem('traaker.conversations.view', v);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (v === 'dashboard') p.set('view', v); else p.delete('view');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const wantsPersonas = searchParams.get('tab') === 'personas';
  useEffect(() => {
    if (!wantsPersonas || loading) return;
    personasPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('tab');
      return p;
    }, { replace: true });
  }, [wantsPersonas, loading, setSearchParams]);

  const { state: scoreState } = useMemo(
    () => computeBrandScore(brandRows, window, mix),
    [brandRows, window, mix],
  );

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('conversations.marqueLoading')}
        </Typography>
      </Box>
    );
  }

  const wLabel = window === '7d' ? t('conversations.period7d') : window === '30d' ? t('conversations.period30d') : t('conversations.period90d');

  return (
    <Box sx={{ bgcolor: '#f8f7fc', minHeight: '100%', mx: -3, px: 3, py: 0.5 }}>
      {/* ① Toolbar: brand name + view toggle (centre) + period selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>{brandName}</Typography>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_, v) => v && setView(v)}
        >
          <ToggleButton value="setup">{t('common.viewSetup')}</ToggleButton>
          <ToggleButton value="dashboard">{t('common.viewDashboard')}</ToggleButton>
        </ToggleButtonGroup>
        {/* Equal-flex side slots keep the view toggle centred in both views */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {view === 'dashboard' && (
            <ToggleButtonGroup
              value={window}
              exclusive
              size="small"
              onChange={(_, v) => v && setWindow(v)}
            >
              <ToggleButton value="7d">{t('conversations.period7d')}</ToggleButton>
              <ToggleButton value="30d">{t('conversations.period30d')}</ToggleButton>
              <ToggleButton value="90d">{t('conversations.period90d')}</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {view === 'dashboard' && (
      <>
      {/* ③ Charts dashboard — filter bar (tag/engine/zone) + timeframe + 5 chart panels */}
      {brandId && (
        <ConversationsDashboard
          brandId={brandId}
          brandRows={brandRows}
          personasSummary={personasSummary}
        />
      )}

      {/* ④ Top 10 personas — last block of the dashboard, read after the aggregate
          charts above: raw per-persona presence, with the persona's description on (i). */}
      {brandRows.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography fontWeight={600}>{t('conversations.top10Title')}</Typography>
            <Select
              size="small"
              value={top10Order}
              onChange={(e) => setTop10Order(e.target.value as typeof top10Order)}
              sx={{ ml: 'auto', fontSize: '0.8125rem' }}
            >
              <MenuItem value="most">{t('conversations.top10Most')}</MenuItem>
              <MenuItem value="least">{t('conversations.top10Least')}</MenuItem>
              <MenuItem value="up">{t('conversations.top10Up')}</MenuItem>
              <MenuItem value="down">{t('conversations.top10Down')}</MenuItem>
            </Select>
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            {(() => {
              // Merge every intent's top10 list, re-cut at 10.
              // `excludedIntents` is deliberately NOT applied here: its control lives in
              // the Configuration view (« Couverture »), and the dashboard's charts above
              // are server-folded and cannot be filtered by intent client-side. Honouring
              // it in this one block would make the dashboard disagree with itself, with
              // no visible control on screen to explain why.
              const all: Array<{ personaId: string; name: string; value: number; delta: number | null; intentId: ConversationIntentId }> = [];
              for (const row of brandRows) {
                const list = row.windows?.[window]?.top10?.[top10Order] ?? [];
                for (const item of list) {
                  all.push({ ...item, intentId: row.intentId });
                }
              }
              // Sort and dedupe (same personaId can appear in multiple intents)
              const seen = new Set<string>();
              const sorted = all
                .sort((a, b) =>
                  (top10Order === 'most' || top10Order === 'up') ? b.value - a.value : a.value - b.value
                )
                .filter(item => { if (seen.has(item.personaId)) return false; seen.add(item.personaId); return true; })
                .slice(0, 10);

              if (sorted.length === 0) {
                return <Typography variant="body2" color="text.secondary">{t('conversations.personasTableNoMeasured')}</Typography>;
              }

              return sorted.map((item, rank) => (
                <Box
                  key={item.personaId}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: rank < sorted.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                >
                  <Typography variant="caption" color="text.disabled" sx={{ width: 20, textAlign: 'right' }}>
                    {rank + 1}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{item.name}</Typography>
                      {/* No icon at all when the persona is gone (archived/deleted) or has
                          no description — never an icon with an empty tooltip. */}
                      {(() => {
                        const description = personaById[item.personaId]?.description?.trim();
                        if (!description) return null;
                        return (
                          <Tooltip
                            title={description}
                            placement="top"
                            slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                          >
                            <InfoOutlinedIcon
                              aria-label={t('conversations.top10PersonaDescription')}
                              sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }}
                            />
                          </Tooltip>
                        );
                      })()}
                    </Box>
                    <Chip
                      label={CONVERSATION_INTENTS[item.intentId]?.name ?? item.intentId}
                      size="small"
                      sx={{ fontSize: '0.625rem', height: 16, bgcolor: INTENT_COLORS[item.intentId] + '18', color: INTENT_COLORS[item.intentId] }}
                    />
                  </Box>
                  <Box sx={{ width: 100 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.round(item.value * 100))}
                      sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { bgcolor: INTENT_COLORS[item.intentId] } }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={700} sx={{ width: 45, textAlign: 'right' }}>
                    {Math.round(item.value * 100)} %
                  </Typography>
                  {item.delta !== null && (
                    <Typography
                      variant="caption"
                      sx={{ width: 50, textAlign: 'right', color: item.delta > 0 ? 'success.main' : item.delta < 0 ? 'error.main' : 'text.secondary' }}
                    >
                      {item.delta > 0 ? '+' : ''}{Math.round(item.delta * 100)} pts
                    </Typography>
                  )}
                </Box>
              ));
            })()}
          </Box>
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontStyle="italic">
              {t('conversations.top10Footnote')}
            </Typography>
          </Box>
        </Paper>
      )}
      </>
      )}

      {view === 'setup' && (
      <>
      {/* ② Coverage warning */}
      {scoreState === 'none' && brandRows.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem', maxWidth: 480 }}>
          <strong>{t('conversations.warnNoScore')}</strong>
        </Alert>
      )}


      {/* ⑧ Personas — the list of the brand's personas; a row opens the persona page.
          Driven by the persona configs, enriched with the `PSUM#` measurement row. */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }} ref={personasPanelRef}>
        <Box sx={{ p: 2, pb: 0, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography fontWeight={600}>{t('conversations.personasTableTitle')}</Typography>
          <Typography variant="caption" color="text.secondary">{wLabel}</Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">{t('conversations.personasTableSort')}</Typography>
            <Select
              size="small"
              value={personasSort}
              onChange={(e) => setPersonasSort(e.target.value as typeof personasSort)}
              sx={{ fontSize: '0.8125rem' }}
            >
              <MenuItem value="presence">{t('conversations.personasTableSortPresence')}</MenuItem>
              <MenuItem value="won">{t('conversations.personasTableSortGagne')}</MenuItem>
              <MenuItem value="delta">{t('conversations.personasTableSortEvolution', { w: wLabel })}</MenuItem>
            </Select>
          </Box>
        </Box>
        {/* Intent tabs */}
        <Tabs
          value={personasTabIntent}
          onChange={(_, v) => setPersonasTabIntent(v)}
          sx={{ px: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`${t('conversations.personasTableAllTab')} (${personas.length})`} value="all" />
          {INTENT_IDS.map(intentId => (
            <Tab
              key={intentId}
              label={`${intentLabel(intentId, t)} (${personas.filter(p => p.intentId === intentId).length})`}
              value={intentId}
              sx={{ color: INTENT_COLORS[intentId] }}
            />
          ))}
        </Tabs>
        {allTags.length > 0 && (
          <Box sx={{ px: 2, py: 1 }}>
            {/* Search input — shown only when there are many tags */}
            {allTags.length > 10 && (
              <TextField
                size="small"
                placeholder="Rechercher un tag…"
                value={tagSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagSearch(e.target.value)}
                sx={{ mb: 0.75, width: 220 }}
                inputProps={{ style: { fontSize: '0.78rem', padding: '4px 8px' } }}
              />
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Tags :</Typography>
              {allTags
                .filter(tag => !tagSearch || tag.includes(tagSearch.toLowerCase()))
                .map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onClick={() => setTagFilter(prev => {
                      const next = new Set(prev);
                      if (next.has(tag)) next.delete(tag); else next.add(tag);
                      return next;
                    })}
                    color={tagFilter.has(tag) ? 'primary' : 'default'}
                    variant={tagFilter.has(tag) ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.6875rem', cursor: 'pointer' }}
                  />
                ))}
              {allTags.length > 10 && !tagSearch && (
                <Typography variant="caption" color="text.disabled">
                  {allTags.length} tags
                </Typography>
              )}
              {tagFilter.size > 0 && (
                <Button size="small" variant="text" sx={{ fontSize: '0.6875rem' }} onClick={() => setTagFilter(new Set())}>
                  Tout afficher
                </Button>
              )}
            </Box>
          </Box>
        )}
        <Divider />
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Persona</TableCell>
                <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{t('conversations.personasTablePresence')}<Tooltip title={t('conversations.personasTablePresenceInfo')} placement="top"><InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></Tooltip></Box></TableCell>
                <TableCell>{t('conversations.personasTable14j')}</TableCell>
                <TableCell>{t('conversations.personasTableByEngine')}</TableCell>
                <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{t('conversations.personasTablePdv')}<Tooltip title={t('conversations.personasTablePdvInfo')} placement="top"><InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></Tooltip></Box></TableCell>
                <TableCell>{t('conversations.personasTableAgents')}</TableCell>
                <TableCell>{t('conversations.personasTableResult')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {personaRows.map(p => (
                <PersonaRow
                  key={p.personaId}
                  persona={p}
                  summary={summaryById[p.personaId]}
                  window={window}
                  showIntentPill={personasTabIntent === 'all'}
                  canRunNow={canRunNow}
                  progress={progressById[p.personaId] ?? null}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                  onRunNow={onRunNow}
                />
              ))}
              {personaRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('conversations.personasTableNone')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
      </>
      )}

    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Conversations() {
  const { t } = useTranslation();
  const { activeBrand: brand } = useBrand();
  const { tenant } = useAuth();

  const [personas, setPersonas] = useState<ConversationPersona[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<ConversationPersona | undefined>(undefined);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  // #28 — tenant-wide gauge usage (from /metering/usage); null while loading
  const [tenantPersonasUsed, setTenantPersonasUsed] = useState<number | null>(null);

  const limits = tenant ? resolveLimits(tenant.tier, tenant.settings?.quotaOverrides) : null;
  const personaQuota = limits?.conversation_personas ?? 0;
  const isAtPerBrandCap = personas.filter((p) => p.status !== 'archived').length >= CONVERSATION_MAX_PERSONAS_PER_BRAND;
  // #28 fix: compare tenant-wide usage against the tenant-wide quota limit
  const isAtQuotaCap = personaQuota !== -1 && tenantPersonasUsed !== null && tenantPersonasUsed >= personaQuota;

  // enterprise and agency can run now (D21)
  const tier = tenant?.tier;
  const canRunNow = tier === 'enterprise' || tier === 'agency';

  const fetchPersonas = useCallback(async () => {
    if (!brand?.brandId) return;
    setLoading(true);
    try {
      const result = await api.get<ConversationPersona[]>(`/brands/${brand.brandId}/personas`);
      setPersonas(result ?? []);
    } catch {
      // Silently ignore — will show empty state.
    } finally {
      setLoading(false);
    }
  }, [brand?.brandId]);

  // #28 — fetch tenant-wide conversation_personas gauge once per brand change
  useEffect(() => {
    if (!brand?.brandId) return;
    api.get<{ metrics: { metric: string; used: number }[] }>('/metering/usage')
      .then((res) => {
        const entry = (res?.metrics ?? []).find((m) => m.metric === 'conversation_personas');
        setTenantPersonasUsed(entry?.used ?? 0);
      })
      .catch(() => setTenantPersonasUsed(0));
  }, [brand?.brandId]);

  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  // Turn-level progress for every persona whose last launch is running (5 s poll).
  // Silent by design: no `loading` flip, no list refetch. State is written ONLY
  // when a number moved, and a finished launch swaps just that persona's card.
  const [progressById, setProgressById] = useState<Record<string, LaunchProgress>>({});
  const runningKey = personas
    .filter((p) => p.lastLaunchStatus === 'running' && p.lastLaunchId)
    .map((p) => `${p.personaId}:${p.lastLaunchId}`)
    .sort()
    .join(',');
  useEffect(() => {
    if (!brand?.brandId || !runningKey) return;
    const brandId = brand.brandId;
    const targets = runningKey.split(',').map((k) => {
      const [personaId, launchId] = k.split(':');
      return { personaId, launchId };
    });
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    // ONE list read settles `lastLaunchStatus` for EVERY persona, so statuses cost a
    // single request no matter how many are running. Patch only the cards whose
    // status actually moved — the rest keep their object identity and don't re-render.
    const refreshStatuses = async (doneIds: Set<string>) => {
      try {
        const list = await api.get<ConversationPersona[]>(`/brands/${brandId}/personas`);
        if (cancelled) return;
        setPersonas((prev) => {
          let changed = false;
          const next = prev.map((p) => {
            const fresh = (list ?? []).find((f) => f.personaId === p.personaId);
            if (!fresh) return p;
            // The progress endpoint said done but the rollup has not stamped
            // 'completed' yet: force it locally, or `runningKey` never changes and
            // this effect polls forever.
            const resolved =
              fresh.lastLaunchStatus === 'running' && doneIds.has(p.personaId)
                ? { ...fresh, lastLaunchStatus: 'completed' as const }
                : fresh;
            if (resolved.lastLaunchStatus === p.lastLaunchStatus) return p;
            changed = true;
            return resolved;
          });
          return changed ? next : prev;
        });
      } catch {
        // keep the current cards; the next tick retries
      }
    };

    // A conversation takes minutes, not seconds — a flat 5 s is ~50 wasted
    // round-trips per launch, so only the first minute polls fast.
    const nextDelay = () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed < 60_000) return 5_000;
      if (elapsed < 300_000) return 15_000;
      return 30_000;
    };

    const poll = async () => {
      // Bound the per-persona fan-out. Past the cap a card simply has no live turn
      // counter; refreshStatuses still flips it out of `running` when it finishes.
      const polled = targets.slice(0, MAX_PROGRESS_POLLS);
      const doneIds = new Set<string>();
      await Promise.all(polled.map(async ({ personaId, launchId }) => {
        try {
          const p = await api.get<LaunchProgress>(
            `/brands/${brandId}/personas/${personaId}/launches/${launchId}/progress`,
          );
          if (cancelled) return;
          setProgressById((prev) => {
            const cur = prev[personaId];
            const same = cur && cur.launchId === p.launchId && cur.done === p.done
              && cur.finalizing === p.finalizing
              && cur.doneTurns === p.doneTurns && cur.totalTurns === p.totalTurns;
            return same ? prev : { ...prev, [personaId]: p };
          });
          if (p.done) doneIds.add(personaId);
        } catch {
          // network hiccup — keep polling
        }
      }));
      if (cancelled) return;
      // Fetch statuses when something finished, or unconditionally when personas
      // past the cap have no progress call of their own to report completion.
      if (doneIds.size > 0 || targets.length > MAX_PROGRESS_POLLS) {
        await refreshStatuses(doneIds);
      }
      if (cancelled) return;
      timer = setTimeout(poll, nextDelay());
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [brand?.brandId, runningKey]);

  const handleCreate = () => {
    setEditingPersona(undefined);
    setEditorOpen(true);
  };

  // Stable handler identities: PersonaCard is memoized, so a progress tick on one
  // card must not hand every other card a new callback.
  const brandIdForHandlers = brand?.brandId;

  const handleEdit = useCallback((p: ConversationPersona) => {
    setEditingPersona(p);
    setEditorOpen(true);
  }, []);

  const handleDuplicate = useCallback(async (p: ConversationPersona) => {
    if (!brandIdForHandlers) return;
    try {
      const result = await api.post<ConversationPersona>(
        `/brands/${brandIdForHandlers}/personas/${p.personaId}/duplicate`,
        {},
      );
      setPersonas((prev) => [result, ...prev]);
      setToast({ message: t('conversations.toastDuplicated'), severity: 'success' });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('conversations.editorErrorGeneric');
      setToast({ message: msg, severity: 'error' });
    }
  }, [brandIdForHandlers, t]);

  const handleDelete = useCallback(async (p: ConversationPersona) => {
    if (!brandIdForHandlers) return;
    if (!window.confirm(t('conversations.deleteConfirm', { name: p.name }))) return;
    try {
      await api.delete(`/brands/${brandIdForHandlers}/personas/${p.personaId}`);
      setPersonas((prev) => prev.filter((x) => x.personaId !== p.personaId));
      setToast({ message: t('conversations.toastDeleted'), severity: 'success' });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('conversations.editorErrorGeneric');
      setToast({ message: msg, severity: 'error' });
    }
  }, [brandIdForHandlers, t]);

  const handleRunNow = useCallback(async (p: ConversationPersona) => {
    if (!brandIdForHandlers) return;
    // D62 — a second launch today replaces today's, without a refund.
    if (hasLaunchToday(p.lastLaunchAt)
      && !window.confirm(t('conversations.runNowSupersedeConfirm'))) return;
    const brand = { brandId: brandIdForHandlers };
    try {
      const result = await api.post<{ launchId: string }>(`/brands/${brand.brandId}/personas/${p.personaId}/run`, {});
      // Flip the card to « en cours » immediately; the poll takes over from here.
      setPersonas((prev) => prev.map((x) => (
        x.personaId === p.personaId ? { ...x, lastLaunchId: result.launchId, lastLaunchStatus: 'running' } : x
      )));
      setToast({ message: t('conversations.toastRunNow'), severity: 'success' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        // Fix 6 — API sends retryAfterMs (ms to wait), not resetAt (an ISO timestamp)
        const resetTime = err.body?.retryAfterMs != null
          ? new Date(Date.now() + err.body.retryAfterMs).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
          : null;
        setToast({
          message: resetTime
            ? t('conversations.editorErrorRateLimitReset', { time: resetTime })
            : t('conversations.editorErrorGeneric'),
          severity: 'error',
        });
      } else {
        setToast({ message: t('conversations.editorErrorGeneric'), severity: 'error' });
      }
    }
  }, [brandIdForHandlers, t]);

  const handleEditorSuccess = (result: ConversationPersona) => {
    const launched = result.lastLaunchStatus === 'running';
    if (editingPersona) {
      setPersonas((prev) => prev.map((p) => (p.personaId === result.personaId ? result : p)));
      setToast({
        message: launched ? t('conversations.toastSavedAndLaunched') : t('conversations.toastUpdated'),
        severity: 'success',
      });
    } else {
      setPersonas((prev) => [result, ...prev]);
      setToast({
        // #40 fix: use the persona's configured scheduleHour instead of a hardcoded time
        message: launched ? t('conversations.toastCreatedAndLaunched') : t('conversations.toastCreated', { time: result.scheduleHour ?? '00:00' }),
        severity: 'success',
      });
    }
  };

  if (!brand) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">{t('conversations.noBrandSelected')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Beta banner */}
      <Alert severity="info" sx={{ mb: 2, fontSize: '0.8125rem' }}>
        {t('conversations.betaBanner')}
      </Alert>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('conversations.pageTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('conversations.pageSubtitle')}
          </Typography>
        </Box>
        <Tooltip
          title={
            isAtPerBrandCap
              ? t('conversations.perBrandCap')
              : isAtQuotaCap
              ? t('conversations.quotaExhausted')
              : ''
          }
          disableHoverListener={!isAtPerBrandCap && !isAtQuotaCap}
        >
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              disabled={isAtPerBrandCap || isAtQuotaCap}
            >
              {t('conversations.createPersona')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* One brand view; its ⑩ « Personas » panel is the persona list (a row opens one). */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
        </Box>
      ) : personas.length === 0 ? (
        <EmptyState onCreateClick={handleCreate} />
      ) : (
        <MarqueView
          brandId={brand.brandId}
          brandName={brand.name ?? ''}
          personas={personas}
          progressById={progressById}
          canRunNow={canRunNow}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          onRunNow={handleRunNow}
        />
      )}

      {/* Editor modal */}
      <PersonaEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        persona={editingPersona}
        brandId={brand.brandId}
        onSuccess={handleEditorSuccess}
      />

      {/* Toast */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
